'use client';

import Link from 'next/link';
import { useActionState, useState, useRef, useMemo, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Upload,
  Building2,
  User,
  Mail,
  Lock,
  ShieldCheck,
  RefreshCw,
  X,
  Globe,
  Phone,
  Search,
  ChevronsUpDown,
  Check,
  Sparkles,
  Coins,
  MapPin,
  Shield,
  CheckCircle2,
  Star,
  Activity,
  Zap,
  Bot,
  MessageSquare,
  BarChart3,
  LockKeyhole,
  CheckCircle,
} from 'lucide-react';
import { COUNTRIES } from '@/lib/countries';
import { WORLD_CURRENCIES } from '@/lib/currencies';
import { signIn } from '@/app/[locale]/(login)/actions';
import { ActionState } from '@/lib/auth/middleware';
import Logo from '@/components/interface/Logo';
import { useBranding } from '@/providers/branding-provider';
import { useTranslations } from 'next-intl';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/theme-switcher';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getPasswordStrength(pass: string): { score: number; label: string; color: string } {
  if (!pass) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pass.length >= 8) score++;
  if (pass.length >= 12) score++;
  if (/[A-Z]/.test(pass) && /[a-z]/.test(pass)) score++;
  if (/\d/.test(pass) || /[^A-Za-z0-9]/.test(pass)) score++;
  if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-red-500' };
  if (score === 2) return { score: 2, label: 'Fair', color: 'bg-amber-500' };
  if (score === 3) return { score: 3, label: 'Strong', color: 'bg-emerald-500' };
  return { score: 4, label: 'Excellent', color: 'bg-teal-500' };
}

function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'workspace'
  );
}

// ─── Country Combobox with Search ─────────────────────────────────────────────

function CountrySelect({
  value,
  onChange,
  placeholder = 'Choose country...',
}: {
  value: string;
  onChange: (countryName: string, dialCode: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCountry = useMemo(() => {
    if (!value) return null;
    return COUNTRIES.find((c) => c.name.toLowerCase() === value.toLowerCase());
  }, [value]);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return COUNTRIES;
    const q = search.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-11 w-full items-center justify-between rounded-xl border border-border/70 bg-background/80 px-3.5 py-2 text-sm shadow-2xs transition-all hover:bg-background focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-left cursor-pointer"
        >
          {selectedCountry ? (
            <span className="flex items-center gap-2 truncate">
              <span className="text-base leading-none">{selectedCountry.flag}</span>
              <span className="truncate font-medium text-foreground">{selectedCountry.name}</span>
              <span className="text-xs text-muted-foreground font-mono ml-auto mr-1">
                {selectedCountry.dialCode}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground/60" />
              <span>{placeholder}</span>
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-40 ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2.5rem)] sm:w-[320px] max-w-[320px] p-0 shadow-2xl rounded-2xl border bg-popover z-[100]" align="start">
        <div className="flex items-center border-b border-border/60 px-3 py-2.5">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search country or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-7 w-full rounded-md bg-transparent text-sm outline-hidden placeholder:text-muted-foreground"
            autoFocus
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-xs text-muted-foreground hover:text-foreground px-1"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto p-1.5">
          {filteredCountries.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No country found.
            </div>
          ) : (
            filteredCountries.map((c) => {
              const isSelected = selectedCountry?.code === c.code;
              return (
                <button
                  key={c.code + c.name}
                  type="button"
                  onClick={() => {
                    onChange(c.name, c.dialCode);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2 text-xs rounded-xl transition-colors cursor-pointer text-left ${
                    isSelected
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className="text-base leading-none">{c.flag}</span>
                    <span className="truncate">{c.name}</span>
                  </span>
                  <span className={`text-[11px] font-mono shrink-0 ml-2 ${isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {c.dialCode}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Currency Combobox with Search ───────────────────────────────────────────

function CurrencySelect({
  value,
  onChange,
  placeholder = 'Choose currency...',
}: {
  value: string;
  onChange: (currencyCode: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCurrency = useMemo(() => {
    if (!value) return null;
    return (
      WORLD_CURRENCIES.find((c) => c.code.toLowerCase() === value.toLowerCase()) || {
        code: value.toUpperCase(),
        name: 'Custom Currency',
        symbol: value.toUpperCase(),
      }
    );
  }, [value]);

  const filteredCurrencies = useMemo(() => {
    if (!search.trim()) return WORLD_CURRENCIES;
    const q = search.toLowerCase();
    return WORLD_CURRENCIES.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-11 w-full items-center justify-between rounded-xl border border-border/70 bg-background/80 px-3.5 py-2 text-sm shadow-2xs transition-all hover:bg-background focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-left cursor-pointer"
        >
          {selectedCurrency ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-mono font-bold text-xs bg-muted px-1.5 py-0.5 rounded border border-border/60 text-foreground">
                {selectedCurrency.symbol || selectedCurrency.code}
              </span>
              <span className="font-semibold text-foreground">{selectedCurrency.code}</span>
              <span className="truncate text-xs text-muted-foreground ml-1">
                {selectedCurrency.name}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground flex items-center gap-2">
              <Coins className="h-4 w-4 text-muted-foreground/60" />
              <span>{placeholder}</span>
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-40 ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2.5rem)] sm:w-[320px] max-w-[320px] p-0 shadow-2xl rounded-2xl border bg-popover z-[100]" align="start">
        <div className="flex items-center border-b border-border/60 px-3 py-2.5">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search currency code or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-7 w-full rounded-md bg-transparent text-sm outline-hidden placeholder:text-muted-foreground"
            autoFocus
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-xs text-muted-foreground hover:text-foreground px-1"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto p-1.5">
          {filteredCurrencies.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No currency found.
            </div>
          ) : (
            filteredCurrencies.map((c) => {
              const isSelected = selectedCurrency?.code === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2 text-xs rounded-xl transition-colors cursor-pointer text-left ${
                    isSelected
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className="font-mono font-bold text-xs bg-muted/80 px-1.5 py-0.5 rounded text-foreground border border-border/50">
                      {c.symbol}
                    </span>
                    <span className="font-medium truncate">{c.name}</span>
                  </span>
                  <span className={`text-xs font-mono font-semibold shrink-0 ml-2 ${isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                    {c.code}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Sign-In Component ───────────────────────────────────────────────────────

function SignInForm() {
  const t = useTranslations('Auth');
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect');
  const priceId = searchParams.get('priceId');
  const invited = searchParams.get('invited');
  const proxyError = searchParams.get('proxyError');
  const [state, formAction, pending] = useActionState<ActionState, FormData>(signIn, { error: '' });
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form className="space-y-4" action={formAction}>
      <input type="hidden" name="redirect" value={redirect || ''} />
      <input type="hidden" name="priceId" value={priceId || ''} />

      {proxyError && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs text-amber-900 dark:text-amber-200 animate-in fade-in space-y-1">
          <p className="font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
            <Lock className="h-3.5 w-3.5" />
            {proxyError === 'already_used'
              ? 'One-Time Link Already Used'
              : proxyError === 'expired'
              ? 'Proxy Login Link Expired'
              : 'Invalid Proxy Login Link'}
          </p>
          <p className="text-amber-800/80 dark:text-amber-200/80">
            {proxyError === 'already_used'
              ? 'For your security, proxy login links can only be used once. Please ask your workspace administrator to generate a new link.'
              : proxyError === 'expired'
              ? 'This link has expired. Please ask your workspace administrator to generate a fresh proxy link.'
              : 'This link is invalid or has been revoked. Please check the URL or request a new one.'}
          </p>
        </div>
      )}

      {invited === '1' && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-800 dark:text-emerald-300">
          <p className="font-semibold flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            Invitation accepted!
          </p>
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
            Your account has been created. Check your email for your login credentials.
          </p>
        </div>
      )}

      {/* Work Email */}
      <div className="space-y-1.5">
        <Label htmlFor="email" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Mail className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
          {t('email_label')}
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          maxLength={50}
          className="h-11 bg-background/80 border-border/70 focus:bg-background focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all"
          placeholder="name@company.com"
          defaultValue={state.email}
        />
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="password" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Lock className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            {t('password_label')}
          </Label>
          <Link href="/forgot-password" className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline font-medium">
            {t('forgot_password')}
          </Link>
        </div>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            minLength={8}
            maxLength={100}
            className="h-11 bg-background/80 border-border/70 focus:bg-background focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all pr-10"
            placeholder="••••••••••••"
            defaultValue={state.password}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {state?.error && (
        <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-xs text-center font-medium border border-destructive/20 animate-in fade-in slide-in-from-top-1">
          {state.error}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 transition-all gap-2 cursor-pointer mt-2"
        disabled={pending}
      >
        {pending ? (
          <>
            <Loader2 className="animate-spin h-4 w-4" />
            {t('signing_in')}
          </>
        ) : (
          <>
            <span>{t('sign_in')}</span>
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>

      {/* Enterprise SSO Hint */}
      <div className="pt-2 text-center">
        <p className="text-[11px] text-muted-foreground">
          Single Sign-On (SSO) and 2FA enforced for enterprise workspaces.
        </p>
      </div>
    </form>
  );
}

// ─── OTP Input Component ─────────────────────────────────────────────────────

function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace' && !inputsRef.current[idx]?.value && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const arr = value.split('');
    arr[idx] = digit;
    const next = arr.join('').padEnd(6, '').slice(0, 6);
    onChange(next);
    if (digit && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text) {
      onChange(text.padEnd(6, '').slice(0, 6));
      const nextIdx = Math.min(text.length, 5);
      inputsRef.current[nextIdx]?.focus();
      e.preventDefault();
    }
  };

  return (
    <div className="flex gap-1.5 sm:gap-2.5 justify-center" onPaste={handlePaste}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onFocus={(e) => e.target.select()}
          className="w-9 sm:w-11 h-12 sm:h-14 text-center text-xl sm:text-2xl font-bold bg-background border-2 border-border/70 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
        />
      ))}
    </div>
  );
}

// ─── Sign-Up Component ───────────────────────────────────────────────────────

function SignUpForm() {
  const t = useTranslations('Auth');
  const router = useRouter();

  // User credentials state
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Company Information state
  const [companyName, setCompanyName] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [currency, setCurrency] = useState('USD');

  // Step 2 state
  const [step, setStep] = useState<1 | 2>(1);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);
  const workspaceSlug = useMemo(() => slugify(companyName), [companyName]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCountryChange = (countryName: string, dialCode: string) => {
    setCountry(countryName);
    if (dialCode) {
      if (!phone || phone.trim() === '' || phone.startsWith('+')) {
        setPhone(dialCode + ' ');
      }
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const sendOtp = async () => {
    if (!companyName.trim() || !ownerName.trim() || !email.trim() || !password) {
      setError('Please fill in all required fields.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('email', email.trim());
      fd.append('password', password);
      fd.append('ownerName', ownerName.trim());
      fd.append('companyName', companyName.trim());
      if (country) fd.append('country', country);
      if (phone) fd.append('phone', phone.trim());
      if (companyAddress) fd.append('companyAddress', companyAddress.trim());
      if (companyWebsite) fd.append('companyWebsite', companyWebsite.trim());
      if (currency) fd.append('currency', currency.trim().toUpperCase());
      if (logoFile) fd.append('companyLogo', logoFile);

      const res = await fetch('/api/auth/send-otp', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to send OTP.');
        return;
      }
      setStep(2);
      setResendCooldown(60);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.replace(/\s/g, '').length < 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp: otp.replace(/\s/g, '') }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Invalid OTP. Please try again.');
        return;
      }
      router.push('/dashboard');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 1: Corporate SaaS Sign-Up Form ────────────────────────────────────
  if (step === 1) {
    return (
      <div className="space-y-5">
        {/* Section 1: Administrator Credentials */}
        <div className="p-4 sm:p-5 rounded-2xl bg-muted/20 border border-border/60 space-y-3.5">
          <div className="flex items-center gap-2 pb-1 border-b border-border/40">
            <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <User className="h-3.5 w-3.5" />
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
                Workspace Administrator
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Your primary credentials used to manage the CRM and sales team
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Owner Name */}
            <div className="space-y-1">
              <Label htmlFor="owner-name" className="text-xs font-semibold text-muted-foreground">
                {t('owner_name_label')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="owner-name"
                type="text"
                required
                maxLength={100}
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all"
                placeholder="Sarah Jenkins"
              />
            </div>

            {/* Work Email */}
            <div className="space-y-1">
              <Label htmlFor="signup-email" className="text-xs font-semibold text-muted-foreground">
                {t('email_label')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="signup-email"
                type="email"
                autoComplete="email"
                required
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all"
                placeholder="sarah@company.com"
              />
            </div>

            {/* Password */}
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="signup-password" className="text-xs font-semibold text-muted-foreground">
                {t('password_label')} <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={100}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all pr-10"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Dynamic Password Strength Indicator */}
              {password && (
                <div className="pt-1.5 space-y-1 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-muted-foreground">Password strength:</span>
                    <span className="font-semibold text-foreground">{passwordStrength.label}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1 h-1.5 w-full bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${passwordStrength.score >= 1 ? passwordStrength.color : 'bg-transparent'}`} />
                    <div className={`h-full rounded-full transition-all ${passwordStrength.score >= 2 ? passwordStrength.color : 'bg-transparent'}`} />
                    <div className={`h-full rounded-full transition-all ${passwordStrength.score >= 3 ? passwordStrength.color : 'bg-transparent'}`} />
                    <div className={`h-full rounded-full transition-all ${passwordStrength.score >= 4 ? passwordStrength.color : 'bg-transparent'}`} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Organization & Workspace */}
        <div className="p-4 sm:p-5 rounded-2xl bg-muted/20 border border-border/60 space-y-3.5">
          <div className="flex items-center gap-2 pb-1 border-b border-border/40">
            <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Building2 className="h-3.5 w-3.5" />
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
                Organization & Workspace
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Branding and configuration for your WhatsApp Cloud API channels
              </p>
            </div>
          </div>

          {/* Logo Dropzone Bar */}
          <div className="flex items-center gap-3.5 p-3 rounded-xl bg-background border border-border/60">
            <div className="relative group shrink-0">
              <div className="w-12 h-12 rounded-xl border-2 border-dashed border-border/80 flex items-center justify-center overflow-hidden cursor-pointer hover:border-emerald-500 transition-all bg-muted/30">
                {logoPreview ? (
                  <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="h-5 w-5 text-muted-foreground/40" />
                )}
                <label
                  htmlFor="company-logo"
                  className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-xl"
                >
                  <Upload className="h-4 w-4 text-white" />
                </label>
              </div>
              {logoPreview && (
                <button
                  type="button"
                  onClick={() => {
                    setLogoPreview(null);
                    setLogoFile(null);
                  }}
                  className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center shadow-xs hover:scale-110 transition-transform"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground">Workspace Logo</span>
                <label
                  htmlFor="company-logo"
                  className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer font-medium"
                >
                  {logoPreview ? 'Change' : 'Upload'}
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground truncate">
                PNG, SVG, or JPG • Displayed in CRM header and invoice receipts
              </p>
            </div>
            <input
              id="company-logo"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Company Name */}
            <div className="space-y-1">
              <Label htmlFor="company-name" className="text-xs font-semibold text-muted-foreground">
                {t('company_name_label')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="company-name"
                type="text"
                required
                maxLength={100}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all"
                placeholder="Acme Global Inc."
              />
              {/* Workspace URL preview */}
              {companyName && (
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-0.5 animate-in fade-in">
                  <span className="text-emerald-600 dark:text-emerald-400 font-mono text-[10px]">●</span>
                  <span>Workspace:</span>
                  <span className="font-mono text-foreground font-medium truncate">zeric.link/{workspaceSlug}</span>
                </div>
              )}
            </div>

            {/* Currency */}
            <div className="space-y-1">
              <Label htmlFor="company-currency" className="text-xs font-semibold text-muted-foreground">
                {t('currency_label') || 'Currency'}
              </Label>
              <CurrencySelect
                value={currency}
                onChange={setCurrency}
                placeholder={t('currency_placeholder') || 'Choose currency...'}
              />
            </div>

            {/* Country Selection */}
            <div className="space-y-1">
              <Label htmlFor="country-select" className="text-xs font-semibold text-muted-foreground">
                {t('country_label')}
              </Label>
              <CountrySelect
                value={country}
                onChange={handleCountryChange}
                placeholder={t('country_placeholder')}
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-1">
              <Label htmlFor="phone-number" className="text-xs font-semibold text-muted-foreground">
                {t('phone_label')}
              </Label>
              <Input
                id="phone-number"
                type="tel"
                maxLength={30}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all font-mono"
                placeholder={t('phone_placeholder')}
              />
            </div>

            {/* Address */}
            <div className="space-y-1">
              <Label htmlFor="company-address" className="text-xs font-semibold text-muted-foreground">
                {t('company_address_label') || 'Company Address'}
              </Label>
              <Input
                id="company-address"
                type="text"
                maxLength={255}
                value={companyAddress}
                onChange={(e) => setCompanyAddress(e.target.value)}
                className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all"
                placeholder="Suite 500, Tech Park"
              />
            </div>

            {/* Website */}
            <div className="space-y-1">
              <Label htmlFor="company-website" className="text-xs font-semibold text-muted-foreground">
                {t('company_website_label') || 'Website'}
              </Label>
              <Input
                id="company-website"
                type="text"
                maxLength={255}
                value={companyWebsite}
                onChange={(e) => setCompanyWebsite(e.target.value)}
                className="h-10 bg-background border-border/70 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-xl transition-all"
                placeholder="https://company.com"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-xs text-center font-medium border border-destructive/20 animate-in fade-in slide-in-from-top-1">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="button"
          onClick={sendOtp}
          disabled={loading}
          className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 transition-all gap-2 cursor-pointer mt-2"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin h-4 w-4" />
              <span>{t('sending_otp')}</span>
            </>
          ) : (
            <>
              <span>Create Organization & Continue</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    );
  }

  // ── Step 2: OTP Verification ──────────────────────────────────────────────
  return (
    <div className="space-y-5 max-w-sm mx-auto py-2">
      {/* Header */}
      <div className="text-center">
        <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <ShieldCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="text-lg font-bold text-foreground">{t('otp_title')}</h2>
        <p className="text-xs text-muted-foreground mt-1">{t('otp_desc')}</p>
        <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5">{email}</p>
      </div>

      {/* OTP Inputs */}
      <div className="py-2">
        <OtpInput value={otp} onChange={setOtp} />
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-xs text-center font-medium border border-destructive/20 animate-in fade-in slide-in-from-top-1">
          {error}
        </div>
      )}

      {/* Verify Button */}
      <Button
        type="button"
        onClick={verifyOtp}
        disabled={loading || otp.length < 6}
        className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 transition-all cursor-pointer"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin mr-2 h-4 w-4" />
            {t('verifying')}
          </>
        ) : (
          t('verify_otp')
        )}
      </Button>

      {/* Resend & Back */}
      <div className="flex items-center justify-between text-xs pt-1">
        <button
          type="button"
          onClick={() => {
            setStep(1);
            setOtp('');
            setError('');
          }}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Back to edit</span>
        </button>

        <div>
          {resendCooldown > 0 ? (
            <span className="text-muted-foreground">
              {t('resend_in', { seconds: resendCooldown })}
            </span>
          ) : (
            <button
              type="button"
              onClick={sendOtp}
              disabled={loading}
              className="text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 font-medium cursor-pointer"
            >
              <RefreshCw className="h-3 w-3" />
              <span>{t('resend_otp')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Login & Signup Page Shell ──────────────────────────────────────────

export function Login({ mode = 'signin' }: { mode?: 'signin' | 'signup' }) {
  const t = useTranslations('Auth');
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect');
  const priceId = searchParams.get('priceId');
  const { branding } = useBranding();
  const siteName = branding?.name || 'Zeric.link';

  return (
    <main className="min-h-screen w-full flex bg-background text-foreground overflow-x-hidden font-sans">
      {/* ── LEFT SHOWCASE HERO (Corporate SaaS Desktop Experience) ────────── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-5/12 flex-col justify-between p-10 xl:p-14 relative overflow-hidden bg-gradient-to-br from-[#0c1319] via-[#0f1b22] to-[#081015] text-white border-r border-border/40 select-none">
        {/* Ambient Glows & Grid Mesh */}
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-emerald-500/15 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] bg-cyan-500/15 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:36px_36px] opacity-35 pointer-events-none" />

        {/* Top Brand Header */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/10">
              <Logo showName={false} size="sm" />
            </div>
            <div>
              <span className="text-xl font-black tracking-tight text-white">{siteName}</span>
              <span className="block text-[10px] font-bold text-emerald-400 tracking-wider uppercase">Enterprise WhatsApp CRM</span>
            </div>
          </div>

          <div className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-semibold text-emerald-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>SOC2 Ready</span>
          </div>
        </div>

        {/* Center: Live Product Simulation & Value Proposition */}
        <div className="relative z-10 my-auto py-6 space-y-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300 font-medium backdrop-blur-md">
              <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
              <span>Unified Omnichannel Revenue Operations</span>
            </div>

            <h2 className="text-3xl xl:text-4xl font-extrabold tracking-tight text-white leading-tight">
              {mode === 'signin'
                ? 'Welcome back to your unified sales engine.'
                : 'Scale WhatsApp sales with verified Meta Cloud API.'}
            </h2>

            <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
              Empower your multi-agent team with AI chatbots, automated e-commerce orders, visual funnel pipelines, and broadcast campaigns.
            </p>
          </div>

          {/* Interactive Live SaaS Product Card */}
          <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-xl shadow-2xl shadow-black/40 space-y-3.5">
            {/* Window header */}
            <div className="flex items-center justify-between pb-2 border-b border-white/10 text-xs text-zinc-400">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                <span className="ml-2 font-mono text-[11px] text-zinc-300">app.zeric.link/inbox</span>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-400 text-[11px] font-mono">
                <Activity className="h-3 w-3 animate-pulse" />
                <span>Meta API: 24ms</span>
              </div>
            </div>

            {/* Simulated Live Thread */}
            <div className="space-y-2.5 text-xs">
              <div className="flex items-start gap-2.5 bg-black/30 p-2.5 rounded-xl border border-white/5">
                <div className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center justify-center shrink-0 text-xs">
                  SJ
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-zinc-200">Sarah Jenkins (VIP Client)</span>
                    <span className="text-[10px] text-zinc-500 font-mono">Just now</span>
                  </div>
                  <p className="text-zinc-400 text-[11px] mt-0.5">
                    "We need to route our 5,000 monthly e-commerce orders to 12 sales reps automatically."
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 bg-emerald-950/40 p-2.5 rounded-xl border border-emerald-500/20 ml-3">
                <div className="w-7 h-7 rounded-full bg-emerald-500 text-slate-950 font-bold flex items-center justify-center shrink-0 text-xs">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-emerald-300">Zeric AI Sales Copilot</span>
                    <span className="text-[10px] text-emerald-400/70 font-mono">Instant</span>
                  </div>
                  <p className="text-emerald-100 text-[11px] mt-0.5">
                    "Deal qualified! Order webhook synced with Shopify. Deal value: <span className="font-bold text-emerald-300">$18,400</span> routed to VIP Pipeline."
                  </p>
                </div>
              </div>
            </div>

            {/* Live Metrics Row */}
            <div className="grid grid-cols-3 gap-2 pt-1">
              <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                <span className="block text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Response</span>
                <span className="text-xs font-bold text-emerald-400">0.8s avg</span>
              </div>
              <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                <span className="block text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Uptime</span>
                <span className="text-xs font-bold text-cyan-400">99.99%</span>
              </div>
              <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                <span className="block text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Active Agents</span>
                <span className="text-xs font-bold text-white">Multi-Seat</span>
              </div>
            </div>
          </div>

          {/* Testimonial Quote */}
          <div className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
            <div className="flex text-amber-400 gap-0.5">
              {'★★★★★'.split('').map((s, i) => (
                <span key={i} className="text-xs">{s}</span>
              ))}
            </div>
            <p className="text-xs text-zinc-300 italic leading-relaxed">
              "Zeric transformed how our 25-person sales and support team handles 10,000+ daily customer WhatsApp chats. Conversion increased by 42% in our first month."
            </p>
            <div className="flex items-center gap-2 pt-0.5">
              <div className="w-5 h-5 rounded-full bg-emerald-500/30 text-emerald-300 text-[10px] font-bold flex items-center justify-center">
                M
              </div>
              <span className="text-[11px] font-semibold text-zinc-200">Marcus Vance</span>
              <span className="text-[10px] text-zinc-500">• Head of Growth, NexaCommerce</span>
            </div>
          </div>
        </div>

        {/* Bottom Enterprise Compliance Bar */}
        <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-zinc-400">
          <div className="flex items-center gap-4 text-[11px] text-zinc-400 font-medium">
            <span className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-emerald-400" />
              <span>256-bit TLS</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-cyan-400" />
              <span>Meta Cloud API</span>
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              <span>GDPR Ready</span>
            </span>
          </div>
          <span className="text-[11px] text-zinc-500">Trusted by 2,500+ businesses</span>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-between p-3 sm:p-8 lg:p-10 relative overflow-y-auto bg-muted/10 dark:bg-background">
        {/* Top Header Row */}
        <div className="flex items-center justify-between w-full max-w-2xl mx-auto pb-3 sm:pb-6">
          <Link href="/">
            <Button variant="ghost" size="sm" className="h-9 px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-xl gap-1.5 cursor-pointer">
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>{t('back_to_home')}</span>
            </Button>
          </Link>

          {/* Mobile Brand Logo */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl flex items-center justify-center">
              <Logo showName={false} size="sm" />
            </div>
            <span className="text-sm font-bold">{siteName}</span>
          </div>

          {/* Right Action Controls: Language & Theme */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
        </div>

        {/* Center Container */}
        <div className={`w-full mx-auto my-auto py-2 sm:py-6 ${mode === 'signup' ? 'max-w-2xl' : 'max-w-md'}`}>
          <div className="bg-card/85 backdrop-blur-2xl border border-border/70 shadow-2xl shadow-black/5 dark:shadow-emerald-500/5 rounded-2xl sm:rounded-3xl p-4 sm:p-8 transition-all animate-in fade-in zoom-in-95 duration-200">
            {/* Top Security Pill */}
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-border/50">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[11px] font-semibold">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>Enterprise 256-bit SSL Enforced</span>
              </div>

              {/* Mode Toggle Tabs */}
              <div className="flex items-center gap-1 p-1 bg-muted/60 rounded-xl border border-border/50">
                <Link
                  href={`/sign-in${redirect ? `?redirect=${redirect}` : ''}${priceId ? `&priceId=${priceId}` : ''}`}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    mode === 'signin'
                      ? 'bg-background text-foreground shadow-2xs font-bold'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  href={`/sign-up${redirect ? `?redirect=${redirect}` : ''}${priceId ? `&priceId=${priceId}` : ''}`}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    mode === 'signup'
                      ? 'bg-background text-foreground shadow-2xs font-bold'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Sign Up
                </Link>
              </div>
            </div>

            {/* Form Title & Subtitle */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                {mode === 'signin' ? t('welcome_back') : t('create_account')}
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed">
                {mode === 'signin' ? t('signin_desc') : t('signup_desc')}
              </p>
            </div>

            {/* Form Body */}
            {mode === 'signin' ? <SignInForm /> : <SignUpForm />}

            {/* Toggle Footer */}
            <div className="mt-6 pt-5 border-t border-border/50 text-center text-xs">
              <span className="text-muted-foreground">
                {mode === 'signin' ? t('no_account') : t('has_account')}
              </span>
              <Link
                href={`${mode === 'signin' ? '/sign-up' : '/sign-in'}${redirect ? `?redirect=${redirect}` : ''}${priceId ? `&priceId=${priceId}` : ''}`}
                className="font-semibold text-emerald-600 dark:text-emerald-400 hover:underline underline-offset-4 ml-1 transition-colors"
              >
                {mode === 'signin' ? t('sign_up') : t('sign_in')}
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Trust & Compliance Footer */}
        <div className="text-center py-4 text-[11px] text-muted-foreground/70 max-w-2xl mx-auto w-full space-y-1">
          <p>Protected by enterprise reCAPTCHA and Zeric Security Guardrails.</p>
          <p>© {new Date().getFullYear()} {siteName}. {t('rights_reserved')}</p>
        </div>
      </div>
    </main>
  );
}